import { useState, useEffect, useCallback } from "react";
import { getDeferredPrompt, clearDeferredPrompt } from "@/pwa/install-capture";

const DISMISS_KEY = "k9_install_dismissed_at";
const DISMISS_DAYS = 14;

const isStandaloneMode = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true)) || false;

const ua = () => (typeof window !== "undefined" ? window.navigator.userAgent || "" : "");
const isIOSDevice = () => {
  const u = ua();
  return /iPad|iPhone|iPod/.test(u) || (u.includes("Macintosh") && typeof document !== "undefined" && "ontouchend" in document);
};
// Chromium-based Android browser: installable even if we never captured beforeinstallprompt.
const isAndroidChromium = () => {
  const u = ua();
  return /Android/.test(u) && /Chrome|CriOS|Chromium|SamsungBrowser|Edg/.test(u) && !/Firefox|FxiOS/.test(u);
};

export function usePwaInstall() {
  const [deferred, setDeferred] = useState(() => getDeferredPrompt());
  const [isStandalone, setIsStandalone] = useState(isStandaloneMode);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return t > 0 && Date.now() - t < DISMISS_DAYS * 864e5;
  });

  useEffect(() => {
    const onBip = (e) => { e.preventDefault?.(); setDeferred(e); };
    const onReady = () => setDeferred(getDeferredPrompt());
    const onInstalled = () => { setInstalled(true); setDeferred(null); clearDeferredPrompt(); };
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onMq = () => setIsStandalone(isStandaloneMode());
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("k9installready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("k9installed", onInstalled);
    mq?.addEventListener?.("change", onMq);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("k9installready", onReady);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("k9installed", onInstalled);
      mq?.removeEventListener?.("change", onMq);
    };
  }, []);

  const ios = isIOSDevice();
  const android = isAndroidChromium();
  const canInstall = !!deferred && !isStandalone && !installed;               // native prompt available
  const iosEligible = ios && !isStandalone && !installed;                     // iOS add-to-home guidance
  const androidFallback = android && !canInstall && !isStandalone && !installed; // installable but event missed
  const unsupported = !canInstall && !iosEligible && !androidFallback && !isStandalone && !installed;

  const promptInstall = useCallback(async () => {
    const dp = deferred || getDeferredPrompt();
    if (!dp) return { outcome: "unavailable" };
    dp.prompt();
    const choice = await dp.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    setDeferred(null);
    clearDeferredPrompt();
    return choice;
  }, [deferred]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }, []);

  return { canInstall, iosEligible, androidFallback, unsupported, isStandalone, installed, dismissed, promptInstall, dismiss };
}
